import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/utils";

type Variant = "primary" | "ghost" | "outlined" | "danger";
type Size = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

const variantClasses: Record<Variant, string> = {
  // #E6E6E6 (ash-50) bg, #2F3031 text — landing page primary CTA
  primary:
    "bg-[var(--color-ash-50)] text-[#2f3031] hover:bg-white shadow-key border-0",
  ghost:
    "bg-transparent text-[var(--color-slate-200)] hover:text-[var(--color-snow)] hover:bg-[var(--color-graphite-700)]",
  outlined:
    "bg-transparent text-[var(--color-snow)] border border-[var(--color-graphite-400)] hover:border-[var(--color-graphite-500)] hover:bg-[var(--color-graphite-700)]",
  danger:
    "bg-transparent text-[var(--color-ember)] border border-[var(--color-ember-dark)] hover:bg-[var(--color-ember-dark)]/40",
};

const sizeClasses: Record<Size, string> = {
  sm: "h-7 px-2.5 text-[13px]",
  md: "h-9 px-3 text-[14px]",
  lg: "h-11 px-4 text-[15px]",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      className,
      variant = "primary",
      size = "md",
      iconLeft,
      iconRight,
      children,
      ...props
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        className={cn(
          "inline-flex items-center justify-center gap-1.5 rounded-[var(--radius-button)] font-medium",
          "transition-[background,color,border-color,box-shadow] duration-200 ease-out",
          "disabled:opacity-40 disabled:cursor-not-allowed",
          variantClasses[variant],
          sizeClasses[size],
          className,
        )}
        {...props}
      >
        {iconLeft}
        {children}
        {iconRight}
      </button>
    );
  },
);
